/// <reference path="../../../../node_modules/monaco-editor/monaco.d.ts" />
import { Component, ElementRef, OnInit, ViewChild, ChangeDetectorRef } from "@angular/core";
import { Title } from "@angular/platform-browser";
import { BaseComponent } from "../../shared/base.component";
import { ApiService } from "src/app/services";
import { LoadingScreenService } from "src/app/shared/loading-screen.service";
import { ToastrService } from "ngx-toastr";
import { ActivatedRoute, Router } from "@angular/router";
import { PlanRegionDetail } from "src/app/generated-models/PlanRegionDetail";
import { PlanSubmission } from "src/app/generated-models/PlanSubmission";
import { PlanRegionUpdateData } from "src/app/generated-models/PlanRegionUpdateData";
import { PlanRegionStatusEnum, PlanValidityEnum } from "src/app/shared/enums/plan-enums";
import { PreprocessDateTime } from "../../utility/view-field-mapping";
import { GridApi, GridOptions , GridReadyEvent } from "ag-grid-community";
import { extendDefaultOptions } from "src/app/utility/gridHelper";
import { getCapacityOrderColumnDefinition, getPlanRegionApprovalsColumnDefinition, PlanDetailGroupColumnDefinition } from "./ag-grid-column-definition";
import { ModalService } from "src/app/shared/modal.service";
import { CapacityOrderRefreshResult } from "src/app/generated-models/CapacityOrderRefreshResult";
import { Region } from "src/app/generated-models";
import { PlanRegionApproval } from "src/app/generated-models/PlanRegionApproval";
import { PlanDetailGroup } from "src/app/generated-models/PlanDetailGroup";
import { PlanRegionWithApprovalStatus } from "src/app/generated-models/PlanRegionWithApprovalStatus";
import { Alternative } from "../../generated-models/Alternative";
import { PlanRegionOverallApproval } from "../../generated-models/PlanRegionOverallApproval";
import { IUserProfile, LionrockUserRoles } from "../../npr-request.model";
import { Observable } from "rxjs";

@Component({
  standalone: false,
  templateUrl: "./plan-detail.component.html",
  styleUrls: ["../../styles.scss", "./plan.scss"],
})
export class PlanDetailComponent extends BaseComponent implements OnInit {
  gridApi: GridApi;
  capacityOrderGridApi: GridApi;
  planDetailGridApi: GridApi;
  editor: monaco.editor.IStandaloneCodeEditor;
  planRegion: PlanRegionDetail;
  region: Region;
  serviceTreeId: string;
  regionName: string;
  planVersion: number;
  latestVersion: number = null;
  planLatestVersionDetailLink: string;
  blueprint: string;
  message: string;
  planFileName: string;
  code: string;
  showRequeueButton: boolean;
  reprocessTooltip: string;
  refreshTooltip: string;
  reprocessExpiredTooltip: string;
  isAdmin = false;
  isGCTApprover = false;
  oldValidToDate: Date | null = null;
  oldValidTo: string | null = null;
  isError = false;
  showCapacityOrders = true;
  requireValidToDate = false;
  processedValidToDate: string = null;
  processedNeedByDate: string = null;
  gridOptions: GridOptions;
  planRegionApprovalsGridOptions: GridOptions;
  planDetailGridOptions: GridOptions;
  planRegionApprovalsRowData: PlanRegionApproval[] = [];
  capacityOrderRowData: CapacityOrderRefreshResult[] = [];
  planDetailRowData: PlanDetailGroup[] = [];
  actionRequired = false;
  readonly planValidityEnumMap = Object.keys(PlanValidityEnum).filter(key => isNaN(Number(key)));
  readonly serviceType = "VM";
  alternatives: Alternative[];
  canApprove = false;
  isServiceUser = false;
  isOperator = false;

  get isAdminOrOperator(): boolean {
    return this.isAdmin || this.isOperator;
  }

  // Add as a class member
  @ViewChild("commentSection", { static: false }) commentSection: ElementRef;
  selectedCommentTopic: string = null;

  constructor(
    private router: Router,
    private readonly route: ActivatedRoute,
    private apiService: ApiService,
    private modalService: ModalService,
    private loadingService: LoadingScreenService,
    private notificationService: ToastrService,
    private title: Title,
    private cdr: ChangeDetectorRef
  ) {
    super();
    this.showRequeueButton = false;
  }

  async ngOnInit() {
    this.loadingService.setLoading(true);
    this.gridOptions = extendDefaultOptions({
      columnDefs: getCapacityOrderColumnDefinition(this.apiService),
      enableRangeSelection: false,
      rowSelection: "single",
      animateRows: true,
      suppressPaginationPanel: true,
      sideBar: null,
    });
    this.planRegionApprovalsGridOptions = extendDefaultOptions({
      columnDefs: getPlanRegionApprovalsColumnDefinition(this.ShowWhoCanApproveDialog.bind(this)),
      enableRangeSelection: false,
      rowSelection: "single",
      animateRows: true,
      suppressPaginationPanel: true,
      sideBar: null,
    });

    this.planDetailGridOptions = extendDefaultOptions({
      columnDefs: PlanDetailGroupColumnDefinition,
      enableRangeSelection: false,
      rowSelection: "single",
      animateRows: true,
      suppressPaginationPanel: true,
      sideBar: null,
    });

    await this.loadPlanDetail();
    await this.loadPlanDetailTable();
    await this.loadAlternatives();

    if (this.planRegion) {
      const userProfile = await this.apiService.getUserProfile();
      if (userProfile?.IsAdmin) {
        this.isAdmin = true;
      }
      if (userProfile?.IsGCTApprover) {
        this.isGCTApprover = true;
      }
      if (userProfile?.IsOperator) {
        this.isOperator = true;
      }
      if (this.planRegion.Status === PlanRegionStatusEnum.Approvable) {
        this.canApprove = userProfile?.IsApprover;
      }

      this.isServiceUser = this.IsServiceUser(userProfile, this.planRegion.ServiceTreeId);
      if (this.planRegion.PlanFile) {
        this.planFileName = this.planRegion.PlanFile.FileName;
        this.code = this.planRegion.PlanFile.Content;
        this.showRequeueButton = this.IsShowRequeueButtonEnabled();
        if (this.showRequeueButton) {
          this.reprocessExpiredTooltip = "Re-process the blueprint file if the file is expired.";
          this.reprocessTooltip = "Re-process the blueprint file if the status is still Created or Error for a long time.";
          this.refreshTooltip = "Refresh the plan region detail as well as the related capacity orders.";
        }
      } else {
        this.router.navigate(["404"], { skipLocationChange: true });
      }

      this.planRegion.ValidToDate = this.planRegion.ValidToDate && new Date(this.planRegion.ValidToDate);
      this.oldValidToDate = this.planRegion.ValidToDate;
      this.oldValidTo = this.planRegion.ValidTo;
      this.processedValidToDate = PreprocessDateTime(this.planRegion.ValidToDate);
      this.processedNeedByDate = PreprocessDateTime(this.planRegion.NeedByDate);

      await this.loadPlanRegionApprovals();
      await this.loadPlanRelatedCapacityOrders();
      this.loadingService.setLoading(false);
      this.cdr.detectChanges();

      setTimeout(() => {
        this.planDetailGridApi?.autoSizeAllColumns();
        this.capacityOrderGridApi?.autoSizeAllColumns();
        this.gridApi?.autoSizeAllColumns();
      }, 100);

      // Add event listener for navigating to comment topic
      document.addEventListener("navigateToCommentTopic", this.handleNavigateToCommentTopic.bind(this));
    }
  }

  onGridReady(_params: GridReadyEvent) {
  }

  onEditorReady(editor: monaco.editor.IStandaloneCodeEditor) {
    this.editor = editor;
    this.editor.getAction("editor.action.formatDocument").run();
  }

  async loadPlanDetail() {
    this.serviceTreeId = this.route.snapshot.params["serviceTreeId"];
    this.regionName = this.route.snapshot.params["region"];
    this.blueprint = this.route.snapshot.params["blueprint"];
    if (this.route.snapshot.params.hasOwnProperty("version")) {
      this.planVersion = this.route.snapshot.params["version"];
    } else {
      try {
        const latestVersionPlanRegion = await this.apiService
          .getPlanRegionLatestVersion(this.serviceTreeId, this.regionName, this.blueprint)
          .toPromise();
        this.planVersion = latestVersionPlanRegion.Version;
      } catch (e) {
        this.notificationService.error(e);
      }
    }
    this.title.setTitle(`${this.route.snapshot.data.title} | ${this.serviceTreeId} | ${this.blueprint} v${this.planVersion} | ${this.regionName}`);
    try {
      this.planRegion = await this.apiService
        .getPlanRegionDetail(this.serviceTreeId, this.regionName, this.planVersion, this.blueprint)
        .toPromise();
      var regions = await this.apiService.getRegionList().toPromise();
      this.actionRequired = this.planRegion.Status == "Action Required";
      this.region = regions.find((r) => r.RegionName == this.regionName);
      this.apiService
        .getPlanRegionLatestVersion(this.planRegion.ServiceTreeId, this.planRegion.Region, this.planRegion.Blueprint)
        .subscribe((response) => {
          this.latestVersion = response.Version;
          if (this.latestVersion != null) {
            this.planLatestVersionDetailLink = `/quota/plans/services/${this.serviceTreeId}/regions/${this.regionName}/versions/${this.latestVersion}/blueprints/${this.blueprint}/detail`;
          }
        });
    } catch (err) {
      this.planRegion = null;
      this.notificationService.error(err);
    }
  }

  async loadPlanDetailTable() {
    return await this.apiService
      .getPlanDetailGroup(this.serviceTreeId, this.regionName, this.planVersion, this.blueprint)
      .toPromise()
      .then((response) => {
        this.planDetailRowData = response;
      })
      .catch((e) => {
        this.notificationService.error(e);
      });
  }

  async loadPlanRegionApprovals() {
    return await this.apiService
      .getRegionalPlanApprovals(this.serviceTreeId, this.regionName, this.planVersion, this.blueprint)
      .toPromise()
      .then((response) => {
        this.planRegionApprovalsRowData = response;
      })
      .catch((e) => {
        this.notificationService.error(e);
      });
  }

  async loadPlanRelatedCapacityOrders() {
    return await this.apiService
      .getRegionalPlanRelatedCapacityOrders(this.serviceTreeId, this.regionName, this.planVersion, this.blueprint)
      .toPromise()
      .then((response) => {
        this.capacityOrderRowData = response;
        if (this.capacityOrderRowData.length == 0) {
          this.showCapacityOrders = false;
        }
      })
      .catch((e) => {
        this.notificationService.error(e);
      });
  }

  async loadAlternatives() {
    return await this.apiService
      .getAlternatives(this.serviceTreeId, this.regionName, this.blueprint, this.planVersion)
      .toPromise()
      .then((response) => {
        this.alternatives = response.filter(item => item.Original !== item.Assigned && item.Assigned);
      })
      .catch((e) => {
        this.notificationService.error(e);
      });
  }
  showRemoveButton(): boolean {
    if (!this.planRegion) {
      return false;
    }

    const status = this.planRegion.Status;
    return (
      ((this.isAdminOrOperator || this.isServiceUser) &&
        (status === PlanRegionStatusEnum.Approvable || status === PlanRegionStatusEnum.Created)) ||
      // Admins can remove failed or stale execution plans stuck in Error status.
      (this.isAdmin && status === PlanRegionStatusEnum.Error)
    );
  }

  async removePlan() {
    const planRegionsToRemove = [{
      ServiceTreeId: this.serviceTreeId,
      Region: this.regionName,
      Version: this.planVersion,
      Blueprint: this.blueprint,
      Status: this.planRegion?.Status
    }] as PlanRegionWithApprovalStatus[];
    await this.modalService.planSetRemovedModal(planRegionsToRemove);
    await this.loadPlanDetail();
  }

  unblockAction(): void {
    const event = new CustomEvent('navigateToCommentTopic');
    document.dispatchEvent(event);
  }

  async requeue() {
    await this.processPlanRegion(
      (payload) => this.apiService.requeuePlanRegion(payload),
      `Plan file will be re-processed soon.`
    );
  }

  async requestReApproval() {
    await this.processPlanRegion(
      (payload) => this.apiService.requeueExpiredPlanRegion(payload),
      `Plan file will be re-submitted for approval soon.`
    );
  }

  private async processPlanRegion(apiCall: (payload: PlanSubmission) => Observable<Object>, successMessage: string) {
    this.loadingService.setLoading(true);
    this.isError = false;
    // Prevent re-processing when the plan is not the latest version — show info popup instead
    if (this.planRegion.PlanVersion < this.latestVersion) {
      this.modalService.validationFailedModal(
        'Re-processing plans that are not the latest version is not supported. Please use the most recent version of the plan.'
      );
      this.loadingService.setLoading(false);
      return;
    }
    const payload: PlanSubmission = {
      ServiceTreeId: this.planRegion.ServiceTreeId,
      Version: this.planRegion.PlanVersion,
      Region: this.planRegion.Region,
      Blueprint: this.planRegion.Blueprint,
    } as PlanSubmission;

    apiCall(payload).subscribe(
      () => {
        this.message = successMessage;
        this.notificationService.info(this.message);
        this.showRequeueButton = false;
      },
      (error: string) => {
        this.message = error;
        this.notificationService.error(this.message);
        this.isError = true;
        this.loadingService.setLoading(false);
      },
      () => {
        this.loadingService.setLoading(false);
      }
    );
  }

  async refresh() {
    try {
      this.capacityOrderRowData = await this.apiService
        .refreshCapacityOrdersByPlanRegionAsync(this.serviceTreeId, this.regionName, this.planVersion, this.blueprint)
        .toPromise();

      this.notificationService.info(`Refresh plan region successfully!`);
      this.gridApi?.refreshCells();
    } catch (err) {
      this.notificationService.error(err);
    }

    try {
      this.planRegion = await this.apiService
        .getPlanRegionDetail(this.serviceTreeId, this.regionName, this.planVersion, this.blueprint)
        .toPromise();
    } catch (err) {
      this.planRegion = null;
      this.notificationService.error(err);
    }
  }

  async showApprovePlanDialog() {
    this.approvePlan();
  }

  approvePlan() {
    const planRegionOverallApproval: PlanRegionOverallApproval = {
      ApprovedVersion: this.planRegion.PlanVersion,
      Blueprint: this.planRegion.Blueprint,
    } as PlanRegionOverallApproval;

    this.loadingService.setLoading(true);

    this.apiService.approvePlanRegion(this.planRegion.ServiceTreeId, this.planRegion.Region, planRegionOverallApproval).subscribe(
      (response) => {
        if (response) {
          this.message = response.Message;
        } else {
          this.message = `Plan have been approved successfully.`;
        }

        this.notificationService.info(this.message);
        this.loadingService.setLoading(false);
      },
      (err) => {
        this.message = `Failed to approve plan. Server response: ${err}`;
        this.notificationService.error(this.message);
        this.loadingService.setLoading(false);
      },
      async () => {
        this.loadingService.setLoading(true);
        await this.loadPlanDetail();
        await this.loadPlanDetailTable();
        await this.loadAlternatives();
        await this.loadPlanRegionApprovals();
        this.loadingService.setLoading(false);
      }
    );
  }

  IsUpdateButtonDisabled(): boolean {
    return this.isError || (!this.ValidToDateUpdated() && !this.ValidToUpdated());
  }

  ValidToDateUpdated(): boolean {
    return this.oldValidToDate?.getTime() !== this.planRegion.ValidToDate?.getTime();
  }

  ValidToUpdated(): boolean {
    return this.oldValidTo !== this.planRegion.ValidTo;
  }

  onCapacityOrderGridReady(params: GridReadyEvent) {
    this.capacityOrderGridApi = params.api;
  }

  onPlanDetailGridReady(params: GridReadyEvent) {
    this.planDetailGridApi = params.api;
  }

  IsShowRequeueButtonEnabled(): boolean {
    return this.isAdminOrOperator || this.isServiceUser;
  }

  IsServiceUser(userProfile: IUserProfile, serviceTreeId: string): boolean {
    return userProfile.UserRoles.some(role => {
      const isServiceRole =
        role.Role === `${LionrockUserRoles.RolePrefix}${LionrockUserRoles.Administrator}` ||
        role.Role === `${LionrockUserRoles.RolePrefix}${LionrockUserRoles.User}`;
      const hasServiceTreeId = Array.isArray(role.Services)
        ? role.Services.includes(serviceTreeId) // If it's an array
        : typeof role.Services === "string" && role.Services.split(',').includes(serviceTreeId); // If it's a string
      return isServiceRole && hasServiceTreeId;
    });
  }

  SubmitChanges(): void {
    if (this.ValidToDateUpdated() || this.ValidToUpdated()) {
      const updateData = {
        ValidToDate: this.planRegion.ValidToDate,
        ValidTo: this.planRegion.ValidTo,
      } as PlanRegionUpdateData;
      this.loadingService.setLoading(true);
      this.apiService
        .updateValidToDate(this.planRegion.ServiceTreeId, this.planRegion.Region, this.planVersion, this.planRegion.Blueprint, updateData)
        .subscribe(
          () => {
            const msg = "Successfully updated valid to date";
            this.oldValidToDate = this.planRegion.ValidToDate;
            this.oldValidTo = this.planRegion.ValidTo;
            this.notificationService.info(msg);
            this.loadingService.setLoading(false);
          },
          (error: unknown) => {
            const message = `Failed to update valid to date, error: ${error}.`;
            this.notificationService.error(message);
            this.loadingService.setLoading(false);
          }
        );
    }
  }

  ShowWhoCanApproveDialog(type: string) {
    this.modalService.resourceApproversModal(type, this.regionName);
  }

  GetValidTo(item: any) {
    if (item === "GA") {
      return "MA";
    }
    return item;
  }

  getStatusDisplay(): string {
    if (this.planRegion?.Status === 'Ready') {
      return 'Ready to Fulfill';
    }
    return this.planRegion?.Status;
  }

  shouldShowFulfillmentLink(): boolean {
    return this.planRegion?.Status === 'Ready';
  }

  onCommentAdded() {
    this.notificationService.info("Comment added successfully");
  }

  ngOnDestroy() {
    // Remove event listener when component is destroyed
    document.removeEventListener("navigateToCommentTopic", this.handleNavigateToCommentTopic.bind(this));
  }

  /**
 * Handles navigation to comment topic from approval grid
 * @param event Custom event containing the resource name to use as topic
 */
  handleNavigateToCommentTopic(event: CustomEvent) {
    const resource = event.detail;
    if (resource) {
      this.selectedCommentTopic = resource;
    } else {
      this.selectedCommentTopic = null;
    }

    // Scroll to comment section
    setTimeout(() => {
      if (this.commentSection?.nativeElement) {
        this.commentSection.nativeElement.scrollIntoView({ behavior: 'smooth' });
      }
    }, 100);
  }
}
